"use client";

import * as React from "react";
import { useNavigate } from "react-router-dom";

import {
  Heading,
  Flex,
  Link,
  Collection,
  Card,
  Badge,
  Grid,
} from "@aws-amplify/ui-react";
import { Helmet } from "react-helmet";

import { generateClient } from "aws-amplify/api";
import { Schema } from "../../../amplify/data/resource";
import { hasAccess } from "../../acl/groups";
import DisciplineImage from "../../components/DisciplineImage";
import { I18n } from "aws-amplify/utils";
import { FaArrowLeft, FaPencil, FaPlus } from "react-icons/fa6";
import LoadingDialog from "../../components/LoadingDialog";

const client = generateClient<Schema>();

type DanceStyle = Schema["DanceStyle"]["type"];

interface DanceStyleProps {
  groups: string[];
}

const DanceStyles: React.FC<DanceStyleProps> = ({ groups }) => {
  const navigate = useNavigate();
  const [danceStylesRecords, setDanceStyles] = React.useState<DanceStyle[]>([]);
  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  React.useEffect(() => {
    const query = hasAccess(groups, "dance-styles", "update")
      ? {}
      : {
          filter: {
            status: { eq: true },
          },
        };

    markLoading("danceStyles", true);

    const danceStylesSub = client.models.DanceStyle.observeQuery(
      query
    ).subscribe({
      next: ({ items }) => {
        setDanceStyles(items);
        markLoading("danceStyles", false);
      },
    });
    return () => {
      danceStylesSub.unsubscribe();
    };
  }, [groups]);

  return (
    <>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex gap="1rem" direction="column">
          <Helmet>
            <title>{I18n.get("Dance Styles")}</title>
          </Helmet>
          <Flex marginTop="1rem">
            <Heading level={1}>{I18n.get("Dance Styles")}</Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>
            {hasAccess(groups, "dance-styles", "create") && (
              <Link href={"/dance-styles/create"}>
                <FaPlus aria-label={I18n.get("Create")} />
              </Link>
            )}
          </Flex>
          <Grid>
            <Collection
              items={danceStylesRecords}
              type="list"
              direction="row"
              wrap="wrap"
              searchPlaceholder={I18n.get("Type to search...")}
              isSearchable
            >
              {(danceStyle: DanceStyle) => (
                <Card
                  key={danceStyle.id}
                  title={danceStyle.name}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Flex direction="column" textAlign="center">
                    <Heading>
                      <Link href={`/dance-styles/${danceStyle.id}/view`}>
                        {danceStyle.name}
                      </Link>
                      {hasAccess(groups, "dance-styles", "update") && (
                        <>
                          &nbsp;{" "}
                          <Link href={`/dance-styles/${danceStyle.id}/update`}>
                            <FaPencil aria-label={I18n.get("Edit")} />
                          </Link>
                        </>
                      )}
                    </Heading>
                    <Link href={`/dance-styles/${danceStyle.id}/view`}>
                      <DisciplineImage
                        discipline={danceStyle.discipline ?? "Standard"}
                      />
                    </Link>
                  </Flex>
                  <Flex
                    direction="row"
                    wrap="wrap"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <Badge
                      backgroundColor={
                        danceStyle.discipline === "Latin"
                          ? "purple.40"
                          : "yellow.40"
                      }
                    >
                      {I18n.get(danceStyle.discipline ?? "Standard")}
                    </Badge>
                    <Badge
                      backgroundColor={
                        danceStyle.status ? "green.40" : "gray.40"
                      }
                    >
                      {danceStyle.status
                        ? I18n.get("Active")
                        : I18n.get("Disabled")}
                    </Badge>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
        </Flex>
      )}
    </>
  );
};

export default DanceStyles;
